import ActionDialog from "@dashboard/components/ActionDialog";
import NotFoundPage from "@dashboard/components/NotFoundPage";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import {
  useApproveBusinessRequestMutation,
  useDenyBusinessRequestMutation,
  useRemoveCustomerMutation,
  useUpdateCustomerMutation,
  useUpdateGeneralInformationMutation,
  useUpdateMetadataMutation,
  useUpdatePrivateMetadataMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { extractMutationErrors, getStringOrPlaceholder } from "@dashboard/misc";
import createMetadataUpdateHandler from "@dashboard/utils/handlers/metadataUpdateHandler";
import { DialogContentText } from "@material-ui/core";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import CustomerDetailsPage, {
  CustomerDetailsPageFormData,
} from "../components/CustomerDetailsPage";
import { useCustomerDetails } from "../hooks/useCustomerDetails";
import { CustomerDetailsProvider } from "../providers/CustomerDetailsProvider";
import {
  businessRequestListUrl,
  customerListUrl,
  customerUrl,
  CustomerUrlQueryParams,
} from "../urls";

interface CustomerDetailsViewProps {
  id: string;
  params: CustomerUrlQueryParams;
}

const CustomerDetailsViewInner: React.FC<CustomerDetailsViewProps> = ({
  id,
  params,
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const customerDetails = useCustomerDetails();
  const user = customerDetails?.customer?.user;
  const customerGroup = customerDetails?.customer?.user?.customerGroup;
  const customerDetailsLoading = customerDetails?.loading;

  const [removeCustomer, removeCustomerOpts] = useRemoveCustomerMutation({
    onCompleted: data => {
      if (data.customerDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            id: "PXatmC",
            defaultMessage: "Customer Removed",
          }),
        });
        navigate(customerListUrl());
      }
    },
  });

  const [updateCustomer, updateCustomerOpts] = useUpdateCustomerMutation({
    onCompleted: data => {
      if (data.customerUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges),
        });
      }
    },
  });

  const [updateMetadata] = useUpdateMetadataMutation({});
  const [updatePrivateMetadata] = useUpdatePrivateMetadataMutation({});
  const [approveBusinessAccess] = useApproveBusinessRequestMutation({});
  const [denyBusinessAccess] = useDenyBusinessRequestMutation({});
  const [updateGeneralInformationMutation] =
    useUpdateGeneralInformationMutation();

  if (user === null) {
    return <NotFoundPage backHref={customerListUrl()} />;
  }

  const updateData = async (data: CustomerDetailsPageFormData) => {
    if (
      data.personalPhone !== user?.generalInformation?.personalPhone &&
      data.personalPhone !== ""
    ) {
      updateGeneralInformation(data, true);
    }
    return extractMutationErrors(
      updateCustomer({
        variables: {
          id,
          input: {
            email: data.email,
            firstName: data.firstName,
            isActive: data.isActive,
            lastName: data.lastName,
            note: data.note,
            hasBusinessAccess: data.hasBusinessAccess,
          },
        },
      }),
    );
  };

  const acceptBusinessRequest = async (customerEmail: string) => {
    try {
      approveBusinessAccess({
        variables: {
          email: customerEmail,
        },
      }).then(res => {
        if (res?.data?.approveBusinessAccess?.errors.length > 0) {
          notify({
            status: "error",
            text: res.data.approveBusinessAccess.errors[0]?.message,
          });
          return;
        }
        if (res?.data?.approveBusinessAccess?.message.includes("Succes")) {
          notify({
            status: "success",
            text: res.data.approveBusinessAccess.message,
          });
          navigate(businessRequestListUrl());
        }
      });
    } catch (err) {
      notify({
        status: "error",
        text: "Something went wrong",
      });
    }
  };

  const denyBusinessRequest = async (customerEmail: string) => {
    try {
      denyBusinessAccess({
        variables: {
          email: customerEmail,
        },
      }).then(res => {
        if (res.data.denyBusinessAccess.errors.length > 0) {
          notify({
            status: "error",
            text: res.data.denyBusinessAccess.errors[0]?.message,
          });
          return;
        }
        if (res.data.denyBusinessAccess.message.includes("Succes")) {
          notify({
            status: "success",
            text: res.data.denyBusinessAccess.message,
          });
          //  refetch();
        }
      });
    } catch (err) {
      notify({
        status: "error",
        text: "Something went wrong",
      });
    }
  };

  const updateGeneralInformation = async (
    formData: CustomerDetailsPageFormData,
    disableNotification?: boolean,
  ) => {
    try {
      if (formData.email !== user.email) {
        notify({
          status: "warning",
          text: "Email is not same as customer email, try to refresh",
        });
        return;
      }
      const { data } = await updateGeneralInformationMutation({
        variables: {
          email: formData.email,
          input: {
            companyName: formData.companyName,
            streetAddress: formData.streetAddress,
            postalCode: formData.postalCode,
            city: formData.city,
            personalPhone: formData.personalPhone,
            businessPhone: formData.businessPhone,
            numberOfWorkers: formData.numberOfWorkers,
            comment: formData.comment,
            UID: formData.UID,
          },
        },
      });

      if (
        data.updateGeneralInformation.errors.length > 0 &&
        !disableNotification
      ) {
        notify({
          status: "error",
          text: data.updateGeneralInformation.errors[0]?.message,
        });
        return;
      }

      if (data.updateGeneralInformation.customer && !disableNotification) {
        notify({
          status: "success",
          text: "Successfully updated B2B informations",
        });
      }
    } catch (err: any) {
      if (disableNotification) {
        return;
      }
      notify({
        status: "error",
        text:
          err?.message.length < 50
            ? err.message
            : "Something went wrong or user has no b2b informations",
      });
    }
  };

  const handleSubmit = createMetadataUpdateHandler(
    user,
    updateData,
    variables => updateMetadata({ variables }),
    variables => updatePrivateMetadata({ variables }),
  );

  return (
    <>
      <WindowTitle title={user?.email} />
      <CustomerDetailsPage
        customerId={id}
        customerGroup={customerGroup}
        acceptBusinessRequest={acceptBusinessRequest}
        denyBusinessRequest={denyBusinessRequest}
        updateGeneralInformation={updateGeneralInformation}
        customer={user}
        disabled={
          customerDetailsLoading ||
          updateCustomerOpts.loading ||
          removeCustomerOpts.loading
        }
        onGroupRemove={customerDetails.refetch}
        errors={updateCustomerOpts.data?.customerUpdate.errors || []}
        saveButtonBar={updateCustomerOpts.status}
        onSubmit={handleSubmit}
        onDelete={() =>
          navigate(
            customerUrl(id, {
              action: "remove",
            }),
          )
        }
      />
      <ActionDialog
        confirmButtonState={removeCustomerOpts.status}
        onClose={() => navigate(customerUrl(id), { replace: true })}
        onConfirm={() =>
          removeCustomer({
            variables: {
              id,
            },
          })
        }
        title={intl.formatMessage({
          id: "ey0lZj",
          defaultMessage: "Delete Customer",
          description: "dialog header",
        })}
        variant="delete"
        open={params.action === "remove"}
      >
        <DialogContentText>
          <FormattedMessage
            id="2p0tZx"
            defaultMessage="Are you sure you want to delete {email}?"
            description="delete customer, dialog content"
            values={{
              email: <strong>{getStringOrPlaceholder(user?.email)}</strong>,
            }}
          />
        </DialogContentText>
      </ActionDialog>
    </>
  );
};

export const CustomerDetailsView: React.FC<CustomerDetailsViewProps> = ({
  id,
  params,
}) => (
  <CustomerDetailsProvider id={id}>
    <CustomerDetailsViewInner id={id} params={params} />
  </CustomerDetailsProvider>
);
export default CustomerDetailsView;
